// Конфигурация обучающей системы «Библиотека → Курс → Тема».
//
// Единственный источник правды для порогов, дефолтов и бюджетов, которые
// упоминаются в `docs/plans/LEARNING-SYSTEM-VISION.md` как «конфиг, не хардкод»
// (§4.3, §12.3). Импортируется и сервером (Inngest / tRPC / квоты), и клиентом
// (пикер формата аудио, глоссарий-слой, офлайн-кэш).
//
// Импорт: `import { MAX_COURSE_TOPICS } from '@studyhub/shared/learning';`
//
// ВАЖНО: значения здесь — продуктовые пороги, а не технические лимиты БД.
// Менять только вместе с планом (VISION §12 «Решения по ранее открытым вопросам»).

import type { SubscriptionTier } from "../constants.js";

// ─── Пороги построения программы курса (VISION §4.3, §12.3, §12.6) ─────────

/**
 * Минимум материалов в составе курса, при котором предлагаем построить программу.
 * Порог «ИЛИ»: достаточно выполнить либо его, либо {@link COURSE_MIN_WORDS}.
 */
export const COURSE_MIN_MATERIALS = 3;

/**
 * Минимум слов суммарно по материалам курса — альтернатива
 * {@link COURSE_MIN_MATERIALS} (один толстый учебник тоже даёт программу).
 */
export const COURSE_MIN_WORDS = 5000;

/**
 * Базовый потолок тем в программе одного курса — значение для состава
 * «до {@link COURSE_TOPICS_LADDER_FROM} материалов». Всё, что сгенерировано
 * сверх потолка, обрезается с предупреждением в `study_plans.description`
 * и `logger.warn({ degradation: 'course-topics-capped' })`.
 *
 * Для конкретного курса потолок считает {@link maxCourseTopicsFor} — на
 * объёмном составе фиксированные 30 тем означали бы 0.3 темы на материал
 * (Фаза 3 GENERATION-CONTEXT-QUALITY-REWORK). Эта константа остаётся
 * дефолтом для мест, где размер состава неизвестен (промо-тексты мастера
 * создания курса), — их поведение не меняется.
 */
export const MAX_COURSE_TOPICS = 30;

/**
 * С какого размера состава потолок тем начинает расти. До этого значения
 * включительно курс получает ровно {@link MAX_COURSE_TOPICS} тем.
 */
export const COURSE_TOPICS_LADDER_FROM = 20;

/** Сколько материалов сверх {@link COURSE_TOPICS_LADDER_FROM} дают +1 тему. */
export const COURSE_TOPICS_MATERIALS_PER_EXTRA_TOPIC = 2;

/**
 * Жёсткий предел потолка тем: курс длиннее 60 тем не проходится ни при каком
 * составе — вместо роста программы пользователю предлагается второй курс
 * (см. {@link COURSE_SPLIT_WARN_MATERIALS}).
 */
export const MAX_COURSE_TOPICS_CAP = 60;

/**
 * Потолок тем для конкретного курса по размеру его состава — ступенчатая
 * лестница: ≤20 материалов → 30 тем, дальше +1 тема на каждые 2 материала
 * сверх 20, но не больше {@link MAX_COURSE_TOPICS_CAP}.
 *
 * Используется и при сборке программы (`generateStudyPlan`), и при достройке
 * тем под новые материалы (`courseCompositionChanged`) — иначе курс, упёршийся
 * в фиксированные 30 тем, молча перестаёт покрывать пополнение состава.
 */
export function maxCourseTopicsFor(materialCount: number): number {
	if (!Number.isFinite(materialCount) || materialCount <= COURSE_TOPICS_LADDER_FROM) {
		return MAX_COURSE_TOPICS;
	}
	const extraTopics = Math.floor(
		(materialCount - COURSE_TOPICS_LADDER_FROM) / COURSE_TOPICS_MATERIALS_PER_EXTRA_TOPIC,
	);
	return Math.min(MAX_COURSE_TOPICS + extraTopics, MAX_COURSE_TOPICS_CAP);
}

/**
 * Порог «курс слишком большой»: при таком количестве материалов в составе
 * показываем предложение разбить курс на два (VISION §12.6).
 */
export const COURSE_SPLIT_WARN_MATERIALS = 50;

/**
 * Целевая ДОЛЯ потолка тем, ниже которой программа считается урезанной.
 *
 * У генерации был только верхний предел («не больше N тем, курс длиннее не
 * проходится»), и модель честно понимала его как разрешение остановиться где
 * угодно: курс по предмету на тридцать возможных тем приходил четырьмя
 * разделами по две темы (жалоба владельца 2026-08-16). Нижняя граница
 * выражена долей, а не числом: потолок сам зависит от состава, и фиксированный
 * минимум либо не дотягивал бы до объёмного курса, либо требовал невозможного
 * от курса из одного материала.
 */
export const COURSE_TOPICS_TARGET_RATIO = 0.6;

/**
 * Абсолютный пол целевого объёма: программа меньше этого не курс, а список дел.
 * Применяется вместе с {@link COURSE_TOPICS_TARGET_RATIO} — берётся большее.
 */
export const MIN_COURSE_TOPICS_TARGET = 12;

/**
 * Сколько тем программа обязана содержать, чтобы считаться полной.
 *
 * Никогда не выше самого потолка: у курса из одного короткого материала
 * потолок и цель совпадают, и требовать от него двенадцати тем бессмысленно.
 */
export function targetCourseTopicsFor(maxTopics: number): number {
	if (!Number.isFinite(maxTopics) || maxTopics <= 0) return 0;
	const byRatio = Math.ceil(maxTopics * COURSE_TOPICS_TARGET_RATIO);
	return Math.min(maxTopics, Math.max(MIN_COURSE_TOPICS_TARGET, byRatio));
}

// ─── Контрольный тест темы, диагностика, тема-закрепление ──────────────────
// (ADAPTIVE-COURSE-LOOP §4.2; решения владельца 1-2 в шапке того же плана)

/**
 * Порог сдачи контрольного теста темы: результат ≥ этого — тема подтверждена.
 *
 * Гейт при этом МЯГКИЙ (решение владельца №1): не сдал — тема всё равно
 * завершается по кнопке, меняется только пометка в маршруте. Порог управляет
 * тем, что показывает экран результатов, а не тем, что разрешено.
 */
export const CHECKPOINT_PASS_PCT = 80;

/**
 * @deprecated Больше НЕ влияет на поведение (решение владельца 2026-08-17).
 *
 * Порог отвечал на вопрос «нужен ли контрольный вообще»: сдал шаг ② на 80+ без
 * пробелов — второй тест подряд не навязывали. Теперь контрольный завершает
 * ЛЮБУЮ тему (`topic-backbone.ts::computeCheckpoint`), поэтому вопроса больше
 * нет. Константа оставлена только как след прежнего правила: на неё ссылаются
 * тесты спины темы, а удаление её из конфига потребовало бы переписывать их
 * ради нуля пользы.
 *
 * Планка СДАЧИ — отдельное решение и живёт в {@link CHECKPOINT_PASS_PCT}.
 */
export const CHECKPOINT_REQUIRED_BELOW_PCT = 80;

/**
 * Входная диагностика: тема с оценкой ≥ этого считается «вы это уже знаете» —
 * зелёная метка в маршруте и экспресс-путь по теме.
 *
 * Порядок разделов диагностика НЕ меняет (решение владельца №4): персонализация
 * идёт метками и глубиной генерации, а не перетасовкой курса.
 */
export const PLACEMENT_STRONG_PCT = 80;

/**
 * Оценка владения темой, ниже которой тема считается слабой при решении о
 * теме-закреплении. Это порог ПРЕДЛОЖЕНИЯ, а не автоматической правки плана:
 * тема-закрепление добавляется карточкой-предложением (решение владельца №5).
 */
export const REMEDIAL_MASTERY_PCT = 50;

/**
 * Сколько слабых тем (по {@link REMEDIAL_MASTERY_PCT}) должно накопиться в
 * разделе, чтобы предложить тему-закрепление. Одна слабая тема — обычный
 * рабочий момент, добор по ней закрывается шагом ③ внутри самой темы.
 */
export const REMEDIAL_MIN_WEAK_TOPICS = 2;

/**
 * Потолок вопросов входного теста. Диагностика опциональна (решение владельца
 * №3) и обязана оставаться коротким ускорителем: 1-2 вопроса на тему, но не
 * длиннее этого числа на весь курс, иначе «быстрый замер» превращается в
 * экзамен, который никто не проходит.
 *
 * Было 15 — ровно тот случай, о котором предупреждает абзац выше: владелец не
 * дошёл до конца собственного теста (2026-08-18). Восемь — потолок, при котором
 * замер ещё успевает потрогать разные модули программы, но остаётся делом одной
 * пары минут. Замер от этого не портится: он и не претендует на точность по
 * каждой теме — темы без вопроса просто остаются без оценки
 * (`assessedMastery === null`), а не считаются нулевыми.
 */
export const PLACEMENT_MAX_QUESTIONS = 8;

// ─── Служебные виды наборов (`material_sets.kind`) ─────────────────────────

/**
 * `material_sets.kind` служебного набора-состава курса.
 * Такой набор не показывается в Библиотеке как папка — он «внутренности» курса
 * и связан с планом через `study_plans.generated_from_set_id`.
 */
export const COURSE_SOURCE_SET_KIND = "course_source" as const;

/** `material_sets.kind` обычной пользовательской папки (значение по умолчанию). */
export const FOLDER_SET_KIND = "folder" as const;

/** Допустимые значения `material_sets.kind`. */
export const MATERIAL_SET_KINDS = [FOLDER_SET_KIND, COURSE_SOURCE_SET_KIND] as const;
export type MaterialSetKind = (typeof MATERIAL_SET_KINDS)[number];

// ─── Комплект темы (VISION §6.1) ───────────────────────────────────────────

/**
 * Текстовые артефакты комплекта темы — в порядке генерации и показа.
 * Аудио в комплект НЕ входит: оно создаётся по требованию (§6.5) либо
 * предгенерируется по выявленному предпочтению (§6.6).
 *
 * Порядок значим: он же порядок активностей `study_subsection_activities`
 * и порядок промпта `generateStudyPlan`.
 *
 * ┌─ Статус после Ш2 модульной платформы ─────────────────────────────────────┐
 * │ Состав БОЛЬШЕ НЕ решается здесь. Кто входит в комплект, объявляет сам      │
 * │ модуль-владелец: `learning.topic.generation: "auto"` в его                 │
 * │ `module.manifest.ts`. Рантайм-состав считает `derivedTopicKitKinds`        │
 * │ (`@studyhub/shared/module`), на сервере он лежит в                         │
 * │ `REGISTRY_TOPIC_KIT_KINDS` (`shared/module-registry.ts`).                  │
 * │                                                                           │
 * │ Кортеж остаётся ЯКОРЕМ ТИПА и не выводится из реестра по той же причине,   │
 * │ по которой §2.8 п.5 запретил динамику у `COURSE_WORKSPACE_VIEWS`: из него  │
 * │ строятся `z.enum` контрактов (`topicKitKindSchema`,                        │
 * │ `templateActivityKindSchema`, `subsectionMaterialKind`), а производный     │
 * │ массив — `string[]`, который превратил бы `TopicKitKind` в `string` и убил │
 * │ вывод типов у tRPC. Расхождение кортежа с реестром ловит гейт              │
 * │ `moduleRegistryDrift()` + `apps/server/src/shared/module-registry.test.ts`.│
 * │                                                                           │
 * │ Добавляешь формат: манифест модуля → строка в этом кортеже. Гейт скажет,   │
 * │ если забыл вторую половину. Третьего места нет.                           │
 * └───────────────────────────────────────────────────────────────────────────┘
 */
export const TOPIC_KIT_KINDS = ["note", "glossary", "flashcards", "quiz", "voice-plan"] as const;
export type TopicKitKind = (typeof TOPIC_KIT_KINDS)[number];

/**
 * Три AI-вызова комплекта: конспект отдельно от глоссария (иначе токены
 * съедает сосед и note получается summarized-обрубком), затем практика.
 * UI «Готовим тему» крутит спиннер на всю текущую группу, остальные — в очереди.
 * Серверный `partitionTopicKitKinds` обязан совпадать (гейт в topic-kit-split).
 */
export const TOPIC_KIT_KIND_GROUPS = [
	["note"],
	["glossary"],
	["flashcards", "quiz", "voice-plan"],
] as const satisfies readonly (readonly TopicKitKind[])[];

// ─── Аудио темы (VISION §6.5, §6.6, §12.8, §12.9) ──────────────────────────

/**
 * Форматы аудио, показываемые в пикере темы. Четвёртый формат `audiobook`
 * намеренно исключён — повествовательный формат для длинного исходника,
 * для темы неуместен (доступен в полном мастере «Настроить подробнее»).
 */
export const TOPIC_RECAP_FORMATS = ["summary", "lecture", "podcast"] as const;
export type TopicRecapFormat = (typeof TOPIC_RECAP_FORMATS)[number];

/**
 * Честная оценка длительности в пикере + целевая длительность генерации.
 *
 * Пересмотр 2026-08-19 (решение владельца): резюме ≤3, лекция ≤10, подкаст ≤5.
 * Раньше подкаст стоял на 12 минутах по логике «диалог двоих раздувает
 * хронометраж» — и именно он оказывался самым долгим в ожидании: синтез идёт
 * примерно вполовину длительности готового аудио, то есть двенадцатиминутный
 * выпуск человек ждал больше пяти минут. Теперь длинный формат ровно один и
 * это лекция: там длительность — содержание, а не форма подачи. Подкаст —
 * короткий разговорный заход в тему, а не выпуск шоу.
 *
 * Цифры отсюда идут И в пикер (подпись «до ~N мин»), И в генерацию — второй
 * копии этой таблицы быть не должно.
 */
export const TOPIC_RECAP_DURATION_SEC = {
	summary: 180,
	lecture: 600,
	podcast: 300,
} as const satisfies Record<TopicRecapFormat, number>;

/**
 * Дефолт пикера — Лекция, а не Подкаст (§12.8): содержание темы уже
 * структурировано, пользователь в учебном режиме.
 */
export const TOPIC_RECAP_DEFAULT_FORMAT = "lecture" as const;

/**
 * Сколько тем подряд человек должен выбрать аудио-канал, чтобы coverage
 * рекомендовал «Слушать» (§6.6, §12.9). Считается в `study_plans.audio_streak`;
 * сбрасывается в 0 при первом выборе текста. JIT N+1 греет краткое резюме
 * всегда — порог на prefetch больше не влияет.
 */
export const AUDIO_PREFERENCE_THRESHOLD = 2;

/**
 * Тарифы, которым включена предгенерация комплекта темы (JIT/prefetch).
 * На обкатке — все (§12.5); при упаковке сузить до `['plus','pro']`.
 */
export const PREFETCH_ENABLED_TIERS = [
	"free",
	"plus",
	"pro",
] as const satisfies readonly SubscriptionTier[];

// ─── Офлайн-кэш аудио (VISION §6.6) ────────────────────────────────────────

/**
 * LRU-бюджет офлайн-кэша аудио по платформам. На web Cache API браузер может
 * выбросить в любой момент — кэш всегда best effort, бейдж «✓ офлайн»
 * рисуется строго по факту `isCached()`, а не по факту постановки в очередь.
 */
export const AUDIO_CACHE_BUDGET_BYTES = {
	web: 300 * 1024 * 1024,
	capacitor: 500 * 1024 * 1024,
} as const;

/**
 * LRU-бюджет офлайн-кэша изображений (иллюстрации материалов, картинки
 * конспекта) — фаза M5.1 плана GENERATION-CONTEXT-QUALITY-REWORK.
 *
 * Отдельный от аудио бюджет и отдельное пространство ключей: вытеснение
 * лекции ради схем из PDF (и наоборот) — не то поведение, которого ждёт
 * пользователь. Числа симметричны аудио-кэшу; на web оба кэша делят квоту
 * origin с PowerSync OPFS, поэтому потолок здесь — именно потолок LRU, а не
 * резервирование места: браузер вправе выбросить Cache API целиком раньше.
 */
export const IMAGE_CACHE_BUDGET_BYTES = {
	web: 300 * 1024 * 1024,
	capacitor: 500 * 1024 * 1024,
} as const;

// ─── Глоссарий-слой (VISION §8.4) ──────────────────────────────────────────

/**
 * Потолок отметок глоссария на один документ. Главный ограничитель против
 * «минного поля из пунктиров»: без него страница при 2000 терминов
 * становится нечитаемой. Дополняется правилами «только первое вхождение
 * термина» и «вне текущего курса — только importance='high'».
 */
export const GLOSSARY_MAX_MARKS_PER_DOC = 25;

// ─── Автозакрытие активностей темы (VISION §4.1) ───────────────────────────

/**
 * Сколько пользователь должен пробыть на конспекте темы, чтобы активность
 * `note` закрылась автоматически (альтернатива — скролл до конца).
 */
export const NOTE_READ_DWELL_MS = 90_000;

/**
 * Доля прослушанного аудио, после которой активность `audio-recap`
 * считается выполненной (закрывается однократно).
 */
export const AUDIO_LISTENED_RATIO = 0.8;

// ─── Квоты комплекта темы (BLUEPRINT §6.9) ─────────────────────────────────

/**
 * Взвешенная стоимость комплекта темы в единицах квоты `ai_generations_daily`.
 *
 * Комплект — это один AI-вызов, порождающий несколько артефактов, поэтому
 * списывать по единице за каждый вид несправедливо. Формула: половина числа
 * видов с округлением вверх, минимум 1 (пять видов → 3 единицы).
 *
 * Значение кладётся в `event.data.quotaAmount` и возвращается тем же числом
 * при refund'е в `onFailure` — иначе квота «протекает» (правило «громкая
 * деградация», CLAUDE.md §14г).
 */
export function weightedTopicKitQuota(kinds: number): number {
	return Math.max(1, Math.ceil(kinds / 2));
}
